import React, { useRef, useState } from "react";
import LogRocket from "logrocket";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Plan } from "../types";
import PaywallInline from "../components/PaywallInline";
import { Button } from "../components/ui/button";
import {
  BillingError,
  createPortalSession,
  getBillingErrorMessage,
  redirectToBillingUrl,
} from "../utils/billingClient";

function Billing() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [portalPending, setPortalPending] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalBlockedReason, setPortalBlockedReason] = useState<
    "review" | "migration" | "not_found" | null
  >(null);
  const portalPendingRef = useRef(false);
  const checkoutResult = searchParams.get("checkout");
  const isPro = currentUser?.plan === "PRO";

  const getPlanDisplay = (plan: Plan | null | undefined) => {
    if (!plan) return "No Plan";
    return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
  };

  const getPlanColor = (plan: Plan | null | undefined) => {
    return plan === "PRO" ? "text-main" : "text-foreground/70";
  };

  return (
    <>
      <h1 className="text-foreground pl-14 pt-10 text-3xl font-bold">
        Billing
      </h1>

      <div className="p-14 flex flex-col gap-10 max-w-[850px]">
        {/* Plan Status Section */}
        <div className="neo-surface border-2 rounded-lg p-8 neo-shadow">
          <h2 className="text-foreground text-xl font-semibold mb-4">
            Current Plan
          </h2>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-foreground/80">Status:</span>
              <span className={getPlanColor(currentUser?.plan)}>
                {getPlanDisplay(currentUser?.plan)}
              </span>
            </div>
          </div>
        </div>

        {checkoutResult === "success" && (
          <div
            role="status"
            className="neo-surface border-2 rounded-lg p-5 neo-shadow"
          >
            {isPro
              ? "Your Pro subscription is active."
              : "Payment received. Your Pro access is being activated; this page will update automatically."}
          </div>
        )}

        {checkoutResult === "canceled" && (
          <div
            role="status"
            className="neo-surface border-2 rounded-lg p-5 neo-shadow"
          >
            Checkout was canceled. You have not been charged.
          </div>
        )}

        {!isPro ? (
          <div className="neo-surface border-2 rounded-lg p-8 neo-shadow">
            <PaywallInline />
          </div>
        ) : (
          <div className="neo-surface border-2 rounded-lg p-8 neo-shadow">
            <h2 className="text-foreground text-xl font-semibold mb-4">
              Manage Plan
            </h2>
            {portalError && (
              <p role="alert" className="mb-4 text-sm text-red-600">
                {portalError}
              </p>
            )}
            <Button
              disabled={portalPending || portalBlockedReason !== null}
              onClick={async () => {
                if (portalPendingRef.current) return;

                portalPendingRef.current = true;
                setPortalPending(true);
                setPortalError(null);
                setPortalBlockedReason(null);
                try {
                  const session = await createPortalSession();
                  LogRocket.log("Stripe billing portal redirect", {
                    requestId: session.requestId,
                  });
                  redirectToBillingUrl(session.url);
                } catch (error) {
                  if (error instanceof BillingError) {
                    if (error.code === "BILLING_REVIEW_REQUIRED") {
                      setPortalBlockedReason("review");
                    } else if (error.code === "CUSTOMER_OWNERSHIP_MISMATCH") {
                      setPortalBlockedReason("migration");
                    } else if (error.code === "CUSTOMER_NOT_FOUND") {
                      setPortalBlockedReason("not_found");
                    }
                  }
                  const normalizedError =
                    error instanceof Error
                      ? error
                      : new Error("Unknown billing error");
                  LogRocket.captureException(normalizedError, {
                    extra: {
                      billingCode:
                        error instanceof BillingError
                          ? error.code
                          : "UNKNOWN_ERROR",
                      requestId:
                        error instanceof BillingError && error.requestId
                          ? error.requestId
                          : "",
                    },
                  });
                  setPortalError(getBillingErrorMessage(error));
                } finally {
                  portalPendingRef.current = false;
                  setPortalPending(false);
                }
              }}
            >
              {portalPending
                ? "Opening billing portal…"
                : portalBlockedReason === "review"
                  ? "Billing review required"
                  : portalBlockedReason === "migration"
                    ? "Migration required"
                    : portalBlockedReason === "not_found"
                      ? "Contact support"
                      : "Manage Billing"}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default Billing;
